import { SectionShell } from "@/components/design-system/SectionShell";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";

const code = `import {
  Table, TableHead, TableBody, TableRow, TableHeaderCell, TableCell, TableEmpty,
} from "@/components/ui/table";

<Table>
  <TableHead>
    <TableRow>
      <TableHeaderCell>Member</TableHeaderCell>
      <TableHeaderCell numeric>Sent</TableHeaderCell>
      <TableHeaderCell>Outcome</TableHeaderCell>
    </TableRow>
  </TableHead>
  <TableBody>
    <TableRow>
      <TableCell nowrap>Dana Staff</TableCell>
      <TableCell numeric>1,204</TableCell>
      <TableCell><Badge tone="accent">Sent</Badge></TableCell>
    </TableRow>
  </TableBody>
</Table>`;

const rows = [
  { member: "F_805068", local: "LU 3", sent: 1204, outcome: "Sent", tone: "accent" },
  { member: "F_805112", local: "LU 12", sent: 0, outcome: "Failed", tone: "danger" },
  { member: "F_805190", local: "LU 913", sent: 0, outcome: "Opted out", tone: "muted" },
] as const;

export function TableSection() {
  return (
    <SectionShell
      id="table"
      title="Table"
      description={
        <>
          A real <code>&lt;table&gt;</code> for dense, multi-column data. The
          primitive renders its own horizontal scroll container, so wide content
          scrolls inside its box and the page body never scrolls sideways.
        </>
      }
      whenToUse={
        <ul>
          <li>Run history, delivery lists, query results — many rows, many columns.</li>
          <li>Anything a reader will scan down a column to compare.</li>
        </ul>
      }
      whenNotToUse={
        <ul>
          <li>
            Key/value detail about one record — use <code>DataTable</code>, the{" "}
            <code>&lt;dl&gt;</code> primitive.
          </li>
          <li>A simple list of things with a title and subtitle — use Listings.</li>
        </ul>
      }
      preview={
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Member</TableHeaderCell>
              <TableHeaderCell>Local</TableHeaderCell>
              <TableHeaderCell numeric>Sent</TableHeaderCell>
              <TableHeaderCell>Outcome</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.member}>
                <TableCell nowrap>{row.member}</TableCell>
                <TableCell nowrap>{row.local}</TableCell>
                <TableCell numeric>{row.sent.toLocaleString()}</TableCell>
                <TableCell>
                  <Badge tone={row.tone}>{row.outcome}</Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      }
      code={code}
      options={
        <ul className="list-disc pl-5">
          <li>
            <code>numeric</code> on <code>TableHeaderCell</code> /{" "}
            <code>TableCell</code>: right-aligns and uses tabular figures, so
            digits line up down the column.
          </li>
          <li>
            <code>nowrap</code> on <code>TableCell</code>: keeps a short value on
            one line. Leave it off for free text so long cells wrap instead of
            widening the table.
          </li>
          <li>
            <code>sticky</code> on <code>TableHeaderCell</code> /{" "}
            <code>TableCell</code>: pins that column against the left edge while
            the rest of the table scrolls under it. For grids wide enough to
            scroll, where losing the row's label leaves the other columns
            meaningless — the Weekly Reports compliance grid uses it for the
            person column. Apply it to the same column in every row, header
            included.
          </li>
          <li>
            <code>TableEmpty</code> renders a centered "nothing here" row —
            pass <code>colSpan</code>.
          </li>
          <li>
            <code>containerClassName</code> on <code>Table</code> reaches the
            scroll container, e.g. to cap its height.
          </li>
        </ul>
      }
    />
  );
}
